import ComponentManager, {ComponentOptions} from "../../componentFramework";
import GeographicMapControls from "../GeographicMapControls";
import Debouncer from "../../scripts/Debouncer";
import JsonScript from "../JsonScript";
import loadScripts from "../../scripts/loadScripts";
import onDocumentLoaded from "../../scripts/onDocumentLoaded";

/**
 */
@ComponentManager.register()
export default class GeographicMap {
	public static componentStyleClass = "geographicMap";

	private static loadingScriptsPromise: Promise<boolean>;
	private static scriptsLoaded: boolean = false;

	private inViewObserver: IntersectionObserver | undefined;

	private googleMap: google.maps.Map | undefined;
	private googleMapMarker: google.maps.Marker | undefined;

	private mapControls: GeographicMapControls | undefined;
	private usersGeoLocation: google.maps.LatLngLiteral | undefined; // Used as a fallback for map rendering

	public static getInstanceFor(element: HTMLElement) {
		return ComponentManager.getComponent(element, GeographicMap);
	}

	constructor(private readonly rootElement: HTMLElement) {
		// TODO Implement use of ComponentOptions.getAttributeName() throughout. Alternatively fully converting to use ComponentOptions would be nice but since we update the attribute value in this class, it would require more rework.

		// Determine Google map key to use.
		const mapKeyAttributeName = ComponentOptions.getAttributeName(this, "key"); // Google Map Key
		let mapKey = rootElement.getAttribute(mapKeyAttributeName);
		if (mapKey === null) {
			const systemConfig = JsonScript.getInstance("config");
			if (systemConfig !== undefined) {
				mapKey = systemConfig.getData()["com.plaudit.core.admin.googleMapsApiKey"];
			}
		}

		if (mapKey === undefined) {
			throw new Error("Map key can not be found to initialize component.");
		}

		// Lazy init: Wait for script to load and map to be in view
		const onScriptsLoaded = () => {
			onDocumentLoaded(rootElement, () => {
				this.inViewObserver = new IntersectionObserver(this.onInView);
				this.inViewObserver.observe(this.rootElement);
			});
			return true;
		}

		// Load script
		if (!GeographicMap.scriptsLoaded) {
			if (GeographicMap.loadingScriptsPromise === undefined) {
				const scriptList = [`https://maps.googleapis.com/maps/api/js?key=${mapKey}`];
				GeographicMap.loadingScriptsPromise = loadScripts(scriptList)
					.then(() => { GeographicMap.scriptsLoaded = true; return true; })
					.catch(reason => {
						console.error("Unable to load JavaScript for Google Maps.", reason);
						return false;
					});
			}
			GeographicMap.loadingScriptsPromise = GeographicMap.loadingScriptsPromise.then(onScriptsLoaded);
		} else {
			onScriptsLoaded();
		}
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.inViewObserver?.disconnect();
		// TODO Should destroy Google Maps too but I can't find documentation on it.
		if (this.googleMap !== undefined) {
			this.googleMap = undefined;
			this.googleMapMarker = undefined;
		}
		this.deregisterControls();
	}

	private onInView = () => {
		this.inViewObserver!.disconnect(); // this.inViewObserver will always be defined here
		this.inViewObserver = undefined;
		this.createMap();
	}

	private loadUsersGeoLocation() {
		if (navigator.geolocation === undefined) {
			this.usersGeoLocation = undefined;
		} else {
			navigator.geolocation.getCurrentPosition(
				(position: GeolocationPosition) => {
					// success
					this.usersGeoLocation = {
						lat: position.coords.latitude,
						lng: position.coords.longitude
					};
					this.createMap();
				},
				() => {
					// Error - Set to null to indicate we tried.
					this.usersGeoLocation = undefined;
				},
				{
					// Options
					enableHighAccuracy: false,
					timeout: 5_000,
					maximumAge: 360_000
				}
			);
		}
	}

	/**
	 * Can be called multiple times. Each time called it will create the map.
	 * @private
	 */
	private createMap() {
		const mapOptions: google.maps.MapOptions = {};

		// Get position
		let position = this.getPosition();
		let positionIsUsingFallback = false;

		if (isNaN(position.lat) || isNaN(position.lng)) {
			if (this.mapControls !== undefined) {
				positionIsUsingFallback = true;
				// If there are map controls connected to this map we want to always display a location.
				// Load user's location and fallback to center of US.
				if (this.usersGeoLocation !== undefined) {
					position = this.usersGeoLocation;
				} else {
					this.loadUsersGeoLocation();
					position = {
						// Center of the US
						lat: 39.8283,
						lng: -98.5795
					};
					mapOptions.zoom = 2;
				}
			} else {
				// Don't have a location and we are not going to fallback because no controls are connected.
				this.googleMap = undefined;
				this.googleMapMarker = undefined;
				return;
			}
		}

		mapOptions.center = position;

		// Configure UI
		mapOptions.disableDefaultUI = true;

		const mapMode = this.rootElement.getAttribute("data-geographicMap-mode");

		let isMarkerDraggable = false;
		this.rootElement.classList.remove("js-geographicMap--disabledInteractions");
		if (mapMode === "draggableMarkerWhenControls" && this.mapControls !== undefined) {
			mapOptions.gestureHandling = "cooperative";
			mapOptions.clickableIcons = false;
			mapOptions.fullscreenControl = false;
			mapOptions.zoomControl = true;
			isMarkerDraggable = true;
		} else {
			mapOptions.gestureHandling = "none";
			this.rootElement.classList.add("js-geographicMap--disabledInteractions");
		}

		// Zoom
		if (mapOptions.zoom === undefined) {
			const zoomAttribute = this.rootElement.getAttribute("data-geographicMap-zoom");
			let zoom = 12;
			if (zoomAttribute !== null) {
				zoom = parseFloat(zoomAttribute);
			}
			mapOptions.zoom = zoom;
		}

		// Initialize
		this.googleMap = new google.maps.Map(this.rootElement, mapOptions);

		// Add marker
		this.createMapMarker(position, positionIsUsingFallback, isMarkerDraggable);
	}

	private createMapMarker(position: google.maps.LatLngLiteral, positionIsUsingFallback: boolean, isMarkerDraggable: boolean) {
		if (position.lat !== null && position.lng !== null) {
			this.googleMapMarker = new google.maps.Marker({
				position: position,
				map: this.googleMap,
				draggable: isMarkerDraggable
			});
			this.googleMapMarker.setOpacity(positionIsUsingFallback ? 0.5 : 1.0);
			if (this.mapControls !== undefined) {
				this.googleMapMarker.setLabel("Drag me");
				const onDrag = new Debouncer({callback: this.onGoogleMapMarkerDrag});
				this.googleMapMarker.addListener("drag", onDrag.run);
				this.googleMapMarker.addListener("dragend", () => this.onGoogleMapMarkerDrag(true));
			}
		} else {
			this.googleMapMarker = undefined;
		}
	}

	private onGoogleMapMarkerDrag = (isDragEnd = false) => {
		if (this.mapControls !== undefined) {
			const markerPosition = this.googleMapMarker?.getPosition();
			if (markerPosition !== null && markerPosition !== undefined) {
				const lat = markerPosition.lat();
				const lng = markerPosition.lng();
				this.mapControls.setLatitude(lat);
				this.mapControls.setLongitude(lng);
				this.savePositionToAttributes(lat, lng);
				if (isDragEnd) {
					this.mapControls.save();
				}
			}
		}
	}

	private getPosition(): google.maps.LatLngLiteral  {
		const latString = this.rootElement.getAttribute("data-geographicMap-lat");
		const positionLat = latString !== null ? parseFloat(latString) : NaN;
		const lonString = this.rootElement.getAttribute("data-geographicMap-long");
		const positionLong = lonString !== null ? parseFloat(lonString) : NaN;
		return {
			lat: positionLat,
			lng: positionLong
		};
	}

	private savePositionToAttributes(lat: number, long: number) {
		this.rootElement.setAttribute("data-geographicMap-lat", lat.toString());
		this.rootElement.setAttribute("data-geographicMap-long", long.toString());
		if (this.googleMapMarker !== undefined) {
			this.googleMapMarker.setOpacity(1.0);
		}
	}
	
	public setPosition(lat: number, long: number) {
		this.savePositionToAttributes(lat, long);

		if (this.googleMap === undefined) {
			// Create map
			this.createMap();
		} else {
			// Update existing map
			const latLong = {
				lat: lat,
				lng: long
			};
			if (this.googleMapMarker !== undefined) {
				this.googleMapMarker.setPosition(latLong);
			}
			this.googleMap.setCenter(latLong);
		}
	}

	public registerControls(mapControls: GeographicMapControls) {
		this.mapControls = mapControls;
		this.createMap();
	}
	public deregisterControls() {
		this.mapControls = undefined;
		this.createMap();
	}
}

